import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router'
import { confirmWeComBinding, getWeComBindingReturnStatus } from '@/api/wecom'
import { track } from '@/plugins/event-tracker'
import { useUserStore } from '@/stores/user'
import { maskString } from '@/utils'
import { isLogin } from '@/utils/auth'

const TOKEN_QUERY_KEYS = ['binding_token', 'bindingToken']
const RELOGIN_QUERY_KEY = 'wecom_relogin'
const WELCOME_LINK_TRACK_KEY_PREFIX = 'qx-wecom-welcome-link-clicked:'

type HandleResult = false | true | RouteLocationRaw

const getQueryValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value.find((item): item is string => typeof item === 'string' && item.length > 0) || ''
  }
  return typeof value === 'string' ? value : ''
}

export const getWeComBindingTokenFromRoute = (to: RouteLocationNormalized): string => {
  for (const key of TOKEN_QUERY_KEYS) {
    const value = getQueryValue(to.query[key])
    if (value) return value
  }
  return ''
}

const cleanWeComReturnQuery = (to: RouteLocationNormalized) => {
  const query: Record<string, string> = {}
  Object.entries(to.query).forEach(([key, value]) => {
    if (TOKEN_QUERY_KEYS.includes(key) || key === RELOGIN_QUERY_KEY) return
    const normalizedValue = getQueryValue(value)
    if (normalizedValue) {
      query[key] = normalizedValue
    }
  })
  return query
}

const buildRedirectFullPath = (to: RouteLocationNormalized, forceRelogin: boolean) => {
  const query = new URLSearchParams()
  Object.entries(to.query).forEach(([key, value]) => {
    if (key === RELOGIN_QUERY_KEY) return
    const normalizedValue = getQueryValue(value)
    if (normalizedValue) {
      query.set(key, normalizedValue)
    }
  })
  if (forceRelogin) {
    query.set(RELOGIN_QUERY_KEY, '1')
  }
  const queryString = query.toString()
  return `${to.path}${queryString ? `?${queryString}` : ''}`
}

const encodeRedirectValue = (redirect: string) => encodeURIComponent(redirect)

const goLogin = (to: RouteLocationNormalized, forceRelogin: boolean): RouteLocationRaw => ({
  path: '/login',
  query: {
    redirect: encodeRedirectValue(buildRedirectFullPath(to, forceRelogin)),
  },
})

const buildReturnTrackParams = (
  to: RouteLocationNormalized,
  bindingToken: string,
  extra?: Record<string, unknown>,
) => {
  const userStore = useUserStore()
  return {
    user_id: userStore.userInfo?.uuid || '',
    scene: 'welcome_return',
    source: getQueryValue(to.query.source),
    binding_token: maskString(bindingToken, { head: 4, tail: 4, mask: '*' }),
    ...extra,
  }
}

type DongchaGuestResolutionEvent =
  | 'dongcha_guest_account_upgrade_success'
  | 'dongcha_guest_existing_account_login_success'

const trackDongchaGuestResolution = (
  eventName: DongchaGuestResolutionEvent,
  to: RouteLocationNormalized,
  bindingToken: string,
  params: Record<string, unknown>,
) => {
  try {
    track(eventName, buildReturnTrackParams(to, bindingToken, params))
  } catch {
    console.warn('[Event Tracker] 洞察企微账号归属上报失败')
  }
}

const trackWelcomeLinkClick = (to: RouteLocationNormalized, bindingToken: string) => {
  const trackEvent = () =>
    track('wecom_welcome_link_click', buildReturnTrackParams(to, bindingToken))
  try {
    const storageKey = `${WELCOME_LINK_TRACK_KEY_PREFIX}${bindingToken}`
    if (sessionStorage.getItem(storageKey)) return
    sessionStorage.setItem(storageKey, '1')
    trackEvent()
  } catch {
    trackEvent()
  }
}

const isBindWecomUseridConflict = (reason?: string) => {
  return (
    reason === 'BIND_WECOM_USERID_BOUND_BY_OTHER_USER' ||
    reason === 'SESSION_BIND_WECOM_USERID_CONFLICT'
  )
}

const trackReturnFailure = (
  to: RouteLocationNormalized,
  bindingToken: string,
  eventName: 'wecom_token_invalid' | 'wecom_external_userid_conflict',
  bindStatus?: string,
  reason?: string,
) => {
  track(
    eventName,
    buildReturnTrackParams(to, bindingToken, {
      result: 'fail',
      bind_status: bindStatus || 'invalid',
      fail_reason: reason || 'TOKEN_INVALID',
    }),
  )
}

export const handleWeComBindingReturn = async (
  to: RouteLocationNormalized,
): Promise<HandleResult> => {
  const bindingToken = getWeComBindingTokenFromRoute(to)
  if (!bindingToken) return false
  trackWelcomeLinkClick(to, bindingToken)

  const userStore = useUserStore()
  const loggedIn = await isLogin()
  if (!loggedIn) {
    return goLogin(to, true)
  }

  const relogin = getQueryValue(to.query[RELOGIN_QUERY_KEY]) === '1'

  if (!relogin) {
    const returnStatus = await getWeComBindingReturnStatus({
      bindingToken,
      source: getQueryValue(to.query.source),
    })
    const action = returnStatus?.action || (returnStatus?.bound ? 'continue' : 'show_error')
    if (action === 'continue') {
      return {
        path: to.path,
        query: cleanWeComReturnQuery(to),
        replace: true,
      }
    }
    if (action === 'show_error') {
      const reason =
        returnStatus?.reason || (returnStatus?.action ? 'TOKEN_INVALID' : 'RETURN_ACTION_EMPTY')
      trackReturnFailure(
        to,
        bindingToken,
        isBindWecomUseridConflict(reason)
          ? 'wecom_external_userid_conflict'
          : 'wecom_token_invalid',
        returnStatus?.bindStatus,
        reason,
      )
      // tcShowToast(returnStatus?.message || '链接已失效，请重新获取二维码')
      return {
        path: to.path,
        query: cleanWeComReturnQuery(to),
        replace: true,
      }
    }
    userStore.clearAuth(false)
    return goLogin(to, true)
  }

  try {
    const result = await confirmWeComBinding({ bindingToken })
    if (result?.success) {
      const alreadyBound = result.reason === 'ALREADY_BOUND'
      if (result.bindStatus === 'active' && !alreadyBound) {
        track(
          'wecom_bind_success',
          buildReturnTrackParams(to, bindingToken, {
            result: 'success',
            bind_status: result.bindStatus || 'active',
          }),
        )
        if (
          result.source?.toLowerCase() === 'dongcha' &&
          result.originUserIdStr &&
          result.userIdStr
        ) {
          const accountUpgraded = result.originUserIdStr === result.userIdStr
          trackDongchaGuestResolution(
            accountUpgraded
              ? 'dongcha_guest_account_upgrade_success'
              : 'dongcha_guest_existing_account_login_success',
            to,
            bindingToken,
            {
              title: accountUpgraded ? '洞察游客账号升级成功' : '洞察游客登录已有账号',
              source: 'dongcha',
              result: 'success',
              user_id: result.userIdStr,
              origin_user_id: result.originUserIdStr,
              target_user_id: result.userIdStr,
              data_merged: false,
              bind_status: result.bindStatus || 'active',
            },
          )
        }
      }
      if (!alreadyBound) {
        // tcShowToast('企业微信绑定成功')
      }
    } else if (result) {
      const reason = result.reason || 'CONFIRM_FAILED'
      if (isBindWecomUseridConflict(reason)) {
        trackReturnFailure(
          to,
          bindingToken,
          'wecom_external_userid_conflict',
          result.bindStatus,
          reason,
        )
      } else if (result.bindStatus !== 'active') {
        trackReturnFailure(to, bindingToken, 'wecom_token_invalid', result.bindStatus, reason)
      }
      if (result.message) {
        // tcShowToast(result.message)
      }
    }
  } catch (error) {
    console.warn('[WeCom] 企业微信绑定确认失败:', error)
    // tcShowToast('企业微信绑定失败，请稍后重试')
  }

  return {
    path: to.path,
    query: cleanWeComReturnQuery(to),
    replace: true,
  }
}
